import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkPlus, Library } from 'lucide-react';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { aiVideoI2vPromptCaseAPI, fundingAuthAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import {
  VIDEO_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_SEED,
  VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoSeed,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import { resolveDefaultTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { buildTeamOwnerNameMap } from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import { toIpeUserErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { mapVideoI2vPromptCaseApiItem } from '@/pages/ipe/workbench/utils/videoI2vPromptCaseLoader';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import VideoI2vPromptCaseLibraryModal from '@/pages/ipe/workbench/components/VideoI2vPromptCaseLibraryModal';
import VideoI2vPromptCaseFormModal from '@/pages/ipe/workbench/modals/VideoI2vPromptCaseFormModal';
import IpeDeleteConfirmModal from '@/pages/ipe/workbench/modals/IpeDeleteConfirmModal';
import '@/styles/ipe/workbench/VideoGeneratePage.css';

/**
 * 图生视频 ·「文本补充」输入区（含选用/新增模板弹窗），与 VideoGeneratePage 侧栏对齐。
 */
export default function VideoI2vSupplementPromptField({
  prompt = '',
  onPromptChange,
  generationMode = VIDEO_DEFAULT_GENERATION_MODE,
  onGenerationModeChange,
  aspectRatio = VIDEO_DEFAULT_ASPECT_RATIO,
  onAspectRatioChange,
  durationSec = VIDEO_DEFAULT_DURATION_SEC,
  onDurationSecChange,
  seed = VIDEO_DEFAULT_SEED,
  onSeedChange,
  disabled = false,
  textareaId = 'video-i2v-supplement-prompt',
  maxLength = VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
  className = '',
  /** 居中弹窗：工作流节点参数面板内使用（功能页默认仍贴左侧参数栏滑入） */
  caseOverlayCentered = false,
}) {
  const { t } = useTranslation('workbench');
  const promptMaxLength = Math.max(1, Number(maxLength) || VIDEO_I2V_SUPPLEMENT_MAX_LENGTH);
  const { user } = useIpeAuth();
  const workspaceMode = useIpeWorkspaceMode();
  const currentUserId = user?.id != null ? String(user.id) : (user?._id != null ? String(user._id) : '');

  const [canAccessTeamPresets, setCanAccessTeamPresets] = useState(false);
  const [teamOwnerNameByUserId, setTeamOwnerNameByUserId] = useState({});
  const [caseListNonce, setCaseListNonce] = useState(0);
  const [caseLibraryOpen, setCaseLibraryOpen] = useState(false);
  const [caseLibraryScope, setCaseLibraryScope] = useState(() => (
    resolveDefaultTitlePresetScope(workspaceMode, false)
  ));
  const [caseSelectedId, setCaseSelectedId] = useState('');
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [caseFormMode, setCaseFormMode] = useState('create');
  const [caseFormScope, setCaseFormScope] = useState('personal');
  const [caseFormEditing, setCaseFormEditing] = useState(null);
  const [caseFormDefaults, setCaseFormDefaults] = useState(null);
  const [caseFormKey, setCaseFormKey] = useState(0);
  const [caseFormSaving, setCaseFormSaving] = useState(false);
  const [caseDeleteTarget, setCaseDeleteTarget] = useState(null);
  const [caseDeleting, setCaseDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then(async (center) => {
        if (cancelled) return;
        const available = isTeamSpaceEntryAvailable(center);
        setCanAccessTeamPresets(available);
        setCaseLibraryScope((prev) => {
          const preferred = resolveDefaultTitlePresetScope(workspaceMode, available);
          if (prev === 'team' && !available) return preferred;
          return prev || preferred;
        });
        if (!available) {
          setTeamOwnerNameByUserId({});
          return;
        }
        try {
          const overview = await fundingAuthAPI.getTeamOverview();
          if (!cancelled) {
            setTeamOwnerNameByUserId(buildTeamOwnerNameMap(overview?.members));
          }
        } catch {
          if (!cancelled) setTeamOwnerNameByUserId({});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessTeamPresets(false);
          setTeamOwnerNameByUserId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const bumpCaseList = useCallback(() => {
    setCaseListNonce((n) => n + 1);
  }, []);

  const setPromptValue = useCallback((value) => {
    onPromptChange?.(String(value || '').slice(0, promptMaxLength));
  }, [onPromptChange, promptMaxLength]);

  const applyStudioParams = useCallback((item) => {
    if (!item) return;
    setPromptValue(item.prompt || '');
    onGenerationModeChange?.(normalizeVideoGenerationMode(item.generationMode));
    onAspectRatioChange?.(normalizeVideoAspectRatio(item.aspectRatio));
    onDurationSecChange?.(normalizeVideoDurationSec(item.durationSec));
    onSeedChange?.(normalizeVideoSeed(item.seed));
  }, [
    setPromptValue,
    onGenerationModeChange,
    onAspectRatioChange,
    onDurationSecChange,
    onSeedChange,
  ]);

  const openCaseFormCreate = useCallback((scope, options = {}) => {
    setCaseLibraryOpen(false);
    setCaseFormMode('create');
    setCaseFormScope(scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(null);
    setCaseFormDefaults(options.prefillFromPrompt
      ? {
        prompt: String(prompt || '').trim(),
        generationMode: normalizeVideoGenerationMode(generationMode),
        aspectRatio: normalizeVideoAspectRatio(aspectRatio),
        durationSec: normalizeVideoDurationSec(durationSec),
        seed: normalizeVideoSeed(seed),
      }
      : null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [prompt, generationMode, aspectRatio, durationSec, seed]);

  const openCaseFormEdit = useCallback((item) => {
    if (!item?.canEdit) {
      showToast(t('studioToast.noPermEdit'), 'warning');
      return;
    }
    setCaseLibraryOpen(false);
    setCaseFormMode('edit');
    setCaseFormScope(item.scope === 'team' ? 'team' : 'personal');
    setCaseFormEditing(item);
    setCaseFormDefaults(null);
    setCaseFormKey((k) => k + 1);
    setCaseFormOpen(true);
  }, [t]);

  const openCaseDelete = useCallback((item) => {
    if (!item?.canDelete) {
      showToast(t('studioToast.noPermDelete'), 'warning');
      return;
    }
    setCaseDeleteTarget(item);
  }, [t]);

  const handleCaseDeleteConfirm = useCallback(async () => {
    const target = caseDeleteTarget;
    if (!target?.id) return;
    setCaseDeleting(true);
    try {
      await aiVideoI2vPromptCaseAPI.delete(target.id);
      showToast(t('studioToast.templateDeleted'), 'success');
      setCaseDeleteTarget(null);
      if (caseFormEditing && String(caseFormEditing.id) === String(target.id)) {
        setCaseFormOpen(false);
        setCaseFormEditing(null);
      }
      if (caseSelectedId === String(target.id)) {
        setCaseSelectedId('');
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateDeleteFailed')), 'error');
    } finally {
      setCaseDeleting(false);
    }
  }, [caseDeleteTarget, caseFormEditing, caseSelectedId, bumpCaseList, t]);

  const handleCaseFormSubmit = useCallback(async (payload) => {
    setCaseFormSaving(true);
    try {
      let savedCase = null;
      if (caseFormMode === 'edit' && caseFormEditing?.id) {
        const { caseScope: _scope, ...updateBody } = payload;
        const res = await aiVideoI2vPromptCaseAPI.update(caseFormEditing.id, updateBody);
        savedCase = mapVideoI2vPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateSaved'), 'success');
      } else {
        const res = await aiVideoI2vPromptCaseAPI.create(payload);
        savedCase = mapVideoI2vPromptCaseApiItem(res?.case);
        showToast(t('studioToast.templateCreated'), 'success');
      }
      if (savedCase) {
        setCaseSelectedId(String(savedCase.id));
        applyStudioParams(savedCase);
      }
      setCaseFormOpen(false);
      setCaseFormEditing(null);
      setCaseFormDefaults(null);
      if (payload.caseScope === 'personal' || payload.caseScope === 'team') {
        setCaseLibraryScope(payload.caseScope);
      }
      bumpCaseList();
    } catch (e) {
      showToast(toIpeUserErrorMessage(e, t('studioToast.templateSaveFailed')), 'error');
    } finally {
      setCaseFormSaving(false);
    }
  }, [caseFormMode, caseFormEditing, bumpCaseList, applyStudioParams, t]);

  const applyCase = useCallback((item) => {
    if (!item) return;
    setCaseSelectedId(String(item.id || ''));
    applyStudioParams(item);
    showToast(t('studioToast.templateApplied'), 'success');
  }, [applyStudioParams, t]);

  const openLibrary = () => {
    if (caseLibraryOpen) {
      setCaseLibraryOpen(false);
      return;
    }
    if (caseFormOpen) setCaseFormOpen(false);
    setCaseLibraryScope(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets),
    );
    setCaseLibraryOpen(true);
  };

  const openCreate = () => {
    if (caseFormOpen && caseFormMode === 'create') {
      setCaseFormOpen(false);
      return;
    }
    openCaseFormCreate(
      resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets) === 'team'
        ? 'team'
        : 'personal',
      { prefillFromPrompt: true },
    );
  };

  const rootClass = [
    'video-studio__section',
    'detail-tool-bridge-modal__video-i2v-prompt',
    className,
  ].filter(Boolean).join(' ');

  return (
    <>
      <section className={rootClass}>
        <div className="video-studio__script-head">
          <label className="video-studio__field-label" htmlFor={textareaId}>
            {t('studioChrome.textExtra')}
          </label>
          <div className="studio-case-actions">
            <button
              type="button"
              className="studio-case-btn"
              onClick={openLibrary}
              disabled={disabled}
            >
              <Library size={14} aria-hidden="true" />
              {t('studioChrome.useCase')}
            </button>
            <button
              type="button"
              className="studio-case-btn"
              onClick={openCreate}
              disabled={disabled}
            >
              <BookmarkPlus size={14} aria-hidden="true" />
              {t('studioChrome.addCase')}
            </button>
          </div>
          <span className={`video-studio__script-count${String(prompt || '').length >= promptMaxLength ? ' is-max' : ''}`}>
            {String(prompt || '').length}/{promptMaxLength}
          </span>
        </div>
        <textarea
          id={textareaId}
          className="video-studio__prompt video-studio__script-input"
          rows={5}
          placeholder={t('studioChrome.textPh')}
          value={prompt}
          onChange={(e) => setPromptValue(e.target.value)}
          maxLength={promptMaxLength}
          disabled={disabled}
        />
      </section>

      <VideoI2vPromptCaseLibraryModal
        isOpen={caseLibraryOpen}
        onClose={() => setCaseLibraryOpen(false)}
        selectedId={caseSelectedId}
        initialScope={caseLibraryScope}
        canAccessTeamPresets={canAccessTeamPresets}
        currentUserId={currentUserId}
        ownerNameByUserId={teamOwnerNameByUserId}
        refreshNonce={caseListNonce}
        escapeDisabled={caseFormOpen || Boolean(caseDeleteTarget)}
        centered={caseOverlayCentered}
        onSelect={applyCase}
        onRequestCreate={openCaseFormCreate}
        onRequestEdit={openCaseFormEdit}
        onRequestDelete={openCaseDelete}
      />
      <VideoI2vPromptCaseFormModal
        key={`detail-bridge-i2v-case-form-${caseFormKey}`}
        isOpen={caseFormOpen}
        mode={caseFormMode}
        initialScope={caseFormScope}
        caseItem={caseFormEditing}
        defaults={caseFormMode === 'create' ? caseFormDefaults : null}
        canAccessTeamPresets={canAccessTeamPresets}
        saving={caseFormSaving}
        centered={caseOverlayCentered}
        onClose={() => {
          if (caseFormSaving) return;
          setCaseFormOpen(false);
          setCaseFormEditing(null);
          setCaseFormDefaults(null);
        }}
        onSubmit={handleCaseFormSubmit}
      />
      <IpeDeleteConfirmModal
        open={Boolean(caseDeleteTarget)}
        title={t('studioChrome.deleteTemplate')}
        confirming={caseDeleting}
        confirmDisabled={caseDeleting}
        onCancel={() => {
          if (caseDeleting) return;
          setCaseDeleteTarget(null);
        }}
        onConfirm={handleCaseDeleteConfirm}
      >
        {caseDeleteTarget
          ? t('studioChrome.deleteTemplateConfirm', { name: caseDeleteTarget.name })
          : ''}
      </IpeDeleteConfirmModal>
    </>
  );
}
